if (typeof debounce === 'undefined') {
  var debounce = function (fn, wait) {
    let timer;

    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu') || this.menu;
      this.toggle = document.querySelector('.mobile-toggle-wrapper');

      this._headerEvents?.abort();
      this._headerResizeObserver?.disconnect();

      if (this._layoutFrame != null) {
        cancelAnimationFrame(this._layoutFrame);
      }

      this._stickyOffset = this._stickyOffset || 0;
      this._needsStickyOffset = true;
      this._layoutFrame = null;

      this._headerEvents = new AbortController();
      this._headerEventOptions = {
        signal: this._headerEvents.signal
      };

      // Keep the drawer outside the header's stacking context.
      const wrapper = document.getElementById('wrapper');

      if (this.menu && wrapper) {
        document.querySelectorAll('.mobile-menu-drawer').forEach((drawer) => {
          if (drawer !== this.menu) {
            drawer.remove();
          }
        });

        if (this.menu.parentElement !== wrapper) {
          wrapper.appendChild(this.menu);
        }
      }

      const mobileToggle = this.toggle?.querySelector('.mobile-toggle');

      if (mobileToggle && this.menu) {
        mobileToggle.addEventListener(
          'click',
          (event) => {
            event.preventDefault();

            if (OverlayManager.isOpen(this.menu)) {
              OverlayManager.close(this.menu);
            } else {
              OverlayManager.open(this.menu);
            }

            window.dispatchEvent(new Event('resize.resize-select'));
          },
          this._headerEventOptions
        );
      }

      document.addEventListener(
        'panel:open',
        (event) => {
          if (this.menu && event.detail?.panel === this.menu) {
            this.toggle?.classList.add('active');
            this.scheduleLayout();
          }
        },
        this._headerEventOptions
      );

      document.addEventListener(
        'panel:closed',
        (event) => {
          if (this.menu && event.detail?.panel === this.menu) {
            this.toggle?.classList.remove('active');
            this.toggle?.removeAttribute('open');
          }
        },
        this._headerEventOptions
      );

      this._onHeaderResize = () => {
        this._needsStickyOffset = true;
        this.scheduleLayout();
      };

      window.addEventListener(
        'resize',
        this._onHeaderResize,
        this._headerEventOptions
      );

      window.addEventListener(
        'scroll',
        () => this.scheduleLayout(),
        {
          passive: true,
          signal: this._headerEvents.signal
        }
      );

      if ('ResizeObserver' in window) {
        this._headerResizeObserver = new ResizeObserver(
          this._onHeaderResize
        );

        this._headerResizeObserver.observe(this);

        const announcementBar = document.querySelector(
          '.announcement-bar-section'
        );

        if (announcementBar) {
          this._headerResizeObserver.observe(announcementBar);
        }
      }

      if (document.fonts) {
        const signal = this._headerEvents.signal;

        document.fonts.ready.then(() => {
          if (this.isConnected && !signal.aborted) {
            this._onHeaderResize();
          }
        });
      }

      this._onSummaryClick = this.onSummaryClick.bind(this);
      this._onCloseButtonClick = this.onCloseButtonClick.bind(this);
      this._boundMenuControls = new WeakSet();

      this.bindMenuControls(this.menu);
      this.scheduleLayout();
    }

    bindMenuControls(container) {
      if (!container) return;

      container.querySelectorAll('summary').forEach((summary) => {
        if (this._boundMenuControls.has(summary)) return;

        summary.addEventListener(
          'click',
          this._onSummaryClick,
          this._headerEventOptions
        );

        this._boundMenuControls.add(summary);
      });

      container.querySelectorAll('.parent-link-back--button').forEach(
        (button) => {
          if (this._boundMenuControls.has(button)) return;

          button.addEventListener(
            'click',
            this._onCloseButtonClick,
            this._headerEventOptions
          );

          this._boundMenuControls.add(button);
        }
      );
    }

    // Retain existing method names for theme compatibility.
    _cacheStickyOffset() {
      this._needsStickyOffset = true;
      this.scheduleLayout();
    }

    setStickyClass() {
      this.scheduleLayout();
    }

    scheduleLayout() {
      if (this._layoutFrame !== null || !this.isConnected) return;

      // Allow a rendering opportunity before measuring layout.
      this._layoutFrame = requestAnimationFrame(() => {
        if (!this.isConnected) {
          this._layoutFrame = null;
          return;
        }

        this._layoutFrame = requestAnimationFrame(() => {
          this._layoutFrame = null;

          if (this.isConnected) {
            this.updateLayout();
          }
        });
      });
    }

    updateLayout() {
      if (!this.isConnected || !this.header_section) return;

      // Collect every layout measurement before making changes.
      const scrollY = window.scrollY;
      const sectionRect = this.header_section.getBoundingClientRect();
      const headerRect = this.getBoundingClientRect();
      const headerHeight = this.clientHeight;

      const announcementBar = document.getElementById(
        'shopify-section-announcement-bar'
      );

      const announcementHeight = announcementBar
        ? announcementBar.clientHeight
        : null;

      const stickyEnabled = this.classList.contains(
        'header-sticky--active'
      );

      const currentlySticky = this.classList.contains('is-sticky');

      if (this._needsStickyOffset) {
        // While sticky, the current position is not the original offset.
        if (stickyEnabled && !currentlySticky) {
          this._stickyOffset = Math.trunc(sectionRect.top) + scrollY;
        }

        this._needsStickyOffset = false;
      }

      const sticky =
        stickyEnabled &&
        scrollY >= this._stickyOffset &&
        scrollY > 0;

      // Re-measure after any geometry change from the sticky class.
      if (stickyEnabled && sticky !== currentlySticky) {
        this.classList.toggle('is-sticky', sticky);
        this.scheduleLayout();
        return;
      }

      const style = document.documentElement.style;

      const write = (name, value) => {
        const px = value + 'px';

        if (style.getPropertyValue(name) !== px) {
          style.setProperty(name, px);
        }
      };

      write('--header-offset', sectionRect.top);
      write('--header-height', headerHeight);
      write('--header-bottom', Math.max(0, headerRect.bottom));

      if (announcementHeight !== null) {
        write('--announcement-height', announcementHeight);
      }
    }

    setAnnouncementHeight() {
      this.scheduleLayout();
    }

    setHeaderOffset() {
      this.scheduleLayout();
    }

    setHeaderHeight() {
      this.scheduleLayout();
    }

    setHeaderBottom() {
      this.scheduleLayout();
    }

    disconnectedCallback() {
      this._headerEvents?.abort();
      this._headerResizeObserver?.disconnect();
      this._headerResizeObserver = null;

      if (this._layoutFrame !== null) {
        cancelAnimationFrame(this._layoutFrame);
      }

      this._layoutFrame = null;
    }

    onSummaryClick(event) {
      if (!this.menu) return;

      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;

      const parentLinksEnabled =
        this.menu.dataset.parentLinks === 'true';

      // When parent links are enabled, only the arrow opens the submenu.
      if (parentLinksEnabled) {
        event.preventDefault();

        const isArrowClick = event.target.closest('.link-forward');

        if (!isArrowClick) {
          const anchor = summaryElement.querySelector('a');

          if (anchor && anchor.href) {
            window.location.href = anchor.href;
          }

          return;
        }

        detailsElement.setAttribute('open', '');
      }

      const parentMenuElement = detailsElement.closest('.link-container');

      const template = detailsElement.querySelector(
        ':scope > template.mobile-menu-deferred'
      );

      if (template) {
        template.replaceWith(template.content.cloneNode(true));
        this.bindMenuControls(detailsElement);
      }

      if (this.menu.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      const signal = this._headerEvents.signal;

      setTimeout(() => {
        if (signal.aborted || !detailsElement.isConnected) return;

        detailsElement.classList.add('menu-opening');

        if (parentMenuElement) {
          parentMenuElement.classList.add('submenu-open');
        }
      }, 100);
    }

    onCloseButtonClick(event) {
      event.preventDefault();

      const detailsElement = event.currentTarget.closest('details');

      if (detailsElement) {
        this.closeSubmenu(detailsElement);
      }
    }

    closeSubmenu(detailsElement) {
      if (!detailsElement) return;

      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }

    closeAnimation(detailsElement) {
      let animationStart;
      const signal = this._headerEvents.signal;

      const handleAnimation = (time) => {
        if (signal.aborted || !detailsElement.isConnected) return;

        if (animationStart === undefined) {
          animationStart = time;
        }

        if (time - animationStart < 400) {
          requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      requestAnimationFrame(handleAnimation);
    }
  }

  customElements.define('theme-header', ThemeHeader);
}

if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    connectedCallback() {
      this._menuEvents?.abort();

      if (this._menuFrame != null) {
        cancelAnimationFrame(this._menuFrame);
      }

      this._menuFrame = null;

      this.submenus = this.querySelectorAll(
        '.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu'
      );

      if (!this.submenus.length) return;

      this._menuEvents = new AbortController();
      const signal = this._menuEvents.signal;

      const schedule = () => {
        if (
          this._menuFrame !== null ||
          !this.isConnected ||
          signal.aborted
        ) {
          return;
        }

        this._menuFrame = requestAnimationFrame(() => {
          if (!this.isConnected || signal.aborted) {
            this._menuFrame = null;
            return;
          }

          this._menuFrame = requestAnimationFrame(() => {
            this._menuFrame = null;

            if (this.isConnected && !signal.aborted) {
              this.resizeSubMenus();
            }
          });
        });
      };

      window.addEventListener('resize', schedule, { signal });

      schedule();

      if (document.fonts) {
        document.fonts.ready.then(schedule);
      }
    }

    disconnectedCallback() {
      this._menuEvents?.abort();

      if (this._menuFrame != null) {
        cancelAnimationFrame(this._menuFrame);
      }

      this._menuFrame = null;
    }

    resizeSubMenus() {
      if (!this.isConnected || !this.submenus) return;

      const viewportWidth = document.body.clientWidth;
      const updates = [];

      // Measure every submenu before changing any classes.
      this.submenus.forEach((submenu) => {
        submenu.querySelectorAll(
          ':scope >.menu-item-has-children>.sub-menu'
        ).forEach((child) => {
          const parent = child.parentElement.parentElement;

          const total =
            child.offsetWidth +
            parent.getBoundingClientRect().left +
            parent.clientWidth +
            10;

          updates.push({
            element: child.parentElement,
            left: total > viewportWidth
          });
        });
      });

      updates.forEach(({ element, left }) => {
        if (element.classList.contains('left-submenu') !== left) {
          element.classList.toggle('left-submenu', left);
        }
      });
    }
  }

  customElements.define('full-menu', FullMenu);
}